<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'

export interface AppTab {
  key: string
  label: string
}

type TabSize = 'sm' | 'md'
/**
 * `segmented` — pill track with a sliding white indicator (default).
 * `underline` — plain labels over a hairline; the active tab gets a bar.
 */
type TabVariant = 'segmented' | 'underline'

interface Props {
  modelValue: string
  tabs: AppTab[]
  size?: TabSize
  variant?: TabVariant
  /** Stretch the control to its parent's full width; tabs share it equally. */
  block?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  variant: 'segmented',
  block: false,
})
defineEmits<{ (e: 'update:modelValue', value: string): void }>()

/**
 * `<button>` elements stay raw — they need DOM refs to measure position for
 * the sliding indicator animation. This is the rare-case exception §19 allows
 * for kit citizens wrapping a native interactive control.
 */
const INDICATOR_PADDING_PX_SM = 3
const INDICATOR_PADDING_PX_MD = 5

const btnRefs = ref<HTMLElement[]>([])
const indicator = ref({ left: 0, width: 0 })

const activeIndex = computed(() =>
  props.tabs.findIndex((tab) => tab.key === props.modelValue),
)

function moveIndicator() {
  const activeButton = btnRefs.value[activeIndex.value]
  if (!activeButton) return
  const parent = activeButton.parentElement
  if (!parent) return
  const parentRect = parent.getBoundingClientRect()
  const rect = activeButton.getBoundingClientRect()
  // The underline bar spans the full label; the segmented pill is inset.
  const segmentedPadding =
    props.size === 'sm' ? INDICATOR_PADDING_PX_SM : INDICATOR_PADDING_PX_MD
  const padding = props.variant === 'underline' ? 0 : segmentedPadding
  indicator.value = {
    left: rect.left - parentRect.left + padding,
    width: rect.width - padding * 2,
  }
}

// In `block` mode the buttons are sized by the parent, so the indicator has to
// be re-measured whenever the track resizes — not only when the tab changes.
let trackObserver: ResizeObserver | null = null

onMounted(async () => {
  await nextTick()
  moveIndicator()
  const track = btnRefs.value[0]?.parentElement
  if (!track) return
  trackObserver = new ResizeObserver(() => moveIndicator())
  trackObserver.observe(track)
})

onBeforeUnmount(() => {
  trackObserver?.disconnect()
  trackObserver = null
})

watch(
  () => props.modelValue,
  () => nextTick(moveIndicator),
)
</script>

<template>
  <Box
    flex
    align="center"
    role="tablist"
    :class="[
      'seg-tabs',
      variant === 'underline' ? 'seg-tabs--underline' : `seg-tabs--${size}`,
      { 'seg-tabs--block': block },
    ]"
  >
    <Box
      class="seg-tabs__indicator"
      :style="{ left: `${indicator.left}px`, width: `${indicator.width}px` }"
    />
    <button
      v-for="(tab, i) in tabs"
      :key="tab.key"
      :ref="
        (el) => {
          if (el) btnRefs[i] = el as HTMLElement
        }
      "
      type="button"
      role="tab"
      :aria-selected="modelValue === tab.key"
      class="seg-tabs__btn"
      :class="{ 'seg-tabs__btn--active': modelValue === tab.key }"
      @click="$emit('update:modelValue', tab.key)"
    >
      <Text is="span" class="seg-tabs__pill">{{ tab.label }}</Text>
    </button>
  </Box>
</template>

<style scoped>
.seg-tabs {
  background: var(--p-surface-100);
  border-radius: 12px;
  flex-shrink: 0;
  position: relative;
  display: inline-flex;
}

/* Full-width segmented control — every tab takes an equal share of the track */
.seg-tabs--block {
  display: flex;
  width: 100%;
}

.seg-tabs--block .seg-tabs__btn {
  flex: 1 1 0;
  min-width: 0;
}

.seg-tabs--block .seg-tabs__pill {
  width: 100%;
  padding: 0;
}

/* Animated sliding indicator */
.seg-tabs__indicator {
  position: absolute;
  background: var(--p-surface-0);
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  transition:
    left 0.25s cubic-bezier(0.4, 0, 0.2, 1),
    width 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  z-index: 0;
}

.seg-tabs__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  outline: none;
  position: relative;
  z-index: 1;
}

.seg-tabs__pill {
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  font-weight: 500;
  color: var(--p-text-color);
  white-space: nowrap;
  line-height: 1;
  transition: color 0.2s ease;
}

.seg-tabs__btn--active .seg-tabs__pill {
  color: var(--p-primary-500);
}

.seg-tabs__btn:not(.seg-tabs__btn--active):hover .seg-tabs__pill {
  color: var(--p-text-color);
}

.seg-tabs__btn:focus-visible {
  outline: 2px solid var(--p-primary-500);
  outline-offset: 2px;
}

/* ── Variant: underline ── */
.seg-tabs--underline {
  background: transparent;
  border-radius: 0;
  gap: 16px;
  border-bottom: 1px solid var(--p-content-border-color);
}

.seg-tabs--underline .seg-tabs__indicator {
  bottom: -1px;
  height: 4px;
  background: var(--p-primary-600);
  border-radius: 100px 100px 0 0;
  box-shadow: none;
}

.seg-tabs--underline .seg-tabs__btn {
  height: 34px;
  padding: 0 0 12px;
}

.seg-tabs--underline .seg-tabs__pill {
  font-size: 0.875rem;
  line-height: 20px;
  color: var(--p-text-muted-color);
}

.seg-tabs--underline .seg-tabs__btn--active .seg-tabs__pill {
  font-weight: 700;
  color: var(--p-primary-600);
}

/* ── Size: md (default) ── */
.seg-tabs--md {
  height: 42px;
  border-radius: 12px;
}

.seg-tabs--md .seg-tabs__indicator {
  top: 5px;
  height: 32px;
  border-radius: 10px;
}

.seg-tabs--md .seg-tabs__btn {
  height: 42px;
  padding: 5px;
  border-radius: 10px;
}

.seg-tabs--md .seg-tabs__pill {
  height: 32px;
  padding: 0 20px;
  font-size: 1rem;
}

/* ── Size: sm ── */
.seg-tabs--sm {
  height: 32px;
  border-radius: 9px;
}

.seg-tabs--sm .seg-tabs__indicator {
  top: 3px;
  height: 26px;
  border-radius: 7px;
}

.seg-tabs--sm .seg-tabs__btn {
  height: 32px;
  padding: 3px;
  border-radius: 7px;
}

.seg-tabs--sm .seg-tabs__pill {
  height: 26px;
  padding: 0 12px;
  font-size: 0.8125rem;
}
</style>
